import React, { ReactNode, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import TuneIcon from "@mui/icons-material/Tune";
import GroupsIcon from "@mui/icons-material/Groups";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpIcon from "@mui/icons-material/Help";
import PersonIcon from "@mui/icons-material/Person";

import { SnackbarProvider } from "../SnackbarContext";
import { BreadcrumbProvider } from "../../context/BreadcrumbContext";
import { Header } from "../../components/layout/Header";
import { Sidebar, SidebarNavItem } from "../../components/layout/Sidebar";
import { AdminAuthService } from "../../services/AdminAuthService";
import { useCompanyData } from "../../hook/useCompanyData";
import { RootState } from "../../store/store";

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
}

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const { company } = useCompanyData();
  const admin = useSelector((state: RootState) => state.adminAuth.admin);

  const firstName = admin?.first_name || "";
  const lastName = admin?.last_name || "";
  const userName = [firstName, lastName].filter(Boolean).join(" ") || admin?.email || "Admin";
  const userInitials = [firstName, lastName]
    .filter(Boolean)
    .map((n: string) => n[0]?.toUpperCase())
    .join("") || "A";

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleCollapseToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const logout = useCallback(() => {
    AdminAuthService.logout(dispatch);
    navigate("/login");
  }, [dispatch, navigate]);

  const navItems: SidebarNavItem[] = [
    { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
    { label: "Models", path: "/models", icon: <ViewInArIcon />, matchPath: "/models" },
    { label: "Options", path: "/options", icon: <TuneIcon />, matchPath: "/options" },
    {
      label: `${process.env.REACT_APP_DEALER_FULL_NAME}s`,
      path: "/dealers",
      icon: <GroupsIcon />,
      matchPath: "/dealers",
    },
    { label: "Quotes", path: "/quotes", icon: <RequestQuoteIcon />, matchPath: "/quote" },
    { label: "FAQ", path: "/faq", icon: <HelpIcon /> },
    { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
    { label: "Profile", path: "/profile", icon: <PersonIcon /> },
  ];

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <BreadcrumbProvider>
    <SnackbarProvider>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        <Sidebar
          navItems={navItems}
          mobileOpen={mobileOpen}
          collapsed={collapsed}
          onToggleMobile={handleDrawerToggle}
          onToggleCollapse={handleCollapseToggle}
          onLogout={logout}
          companyLogo={company?.company_logo}
          companyName={company?.company_name}
          userName={userName}
          userInitials={userInitials}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            transition: "width 0.2s ease",
          }}
        >
          <Header
            title={title}
            handleDrawerToggle={handleDrawerToggle}
            logout={logout}
            userName={userName}
            userInitials={userInitials}
            sidebarCollapsed={collapsed}
          />
          <Box sx={{ pt: "88px", px: 3, pb: 3 }}>
            {children}
          </Box>
        </Box>
      </Box>
    </SnackbarProvider>
    </BreadcrumbProvider>
  );
};
